// release-deploy-outcome-manual-resolution-targets.ts
//
// Which deploy intents a `resolve-deploy` answer applies to, and the refusals
// that come from the journal alone (release-deploy-outcome-manual-resolution.ts
// keeps the actor/lock/lease checks, the receipt handling and the write). Split
// out to stay under the 200-line budget when review 261001 M1 added a rule.
//
//   - `matching`    — deploys of the named candidate the journal cannot settle
//     (no result, timed_out, killed, an unbound legacy `succeeded`);
//   - `retractable` — only for `not-deployed`: deploys of that candidate whose
//     CURRENT result is a person's `deployed` answer AND whose deploy hash is
//     the current one. A manual answer made under another config.release is no
//     longer in force (`run` deployed again under the new hash and wrote its
//     own receipt), so taking it back would change nothing while saying it did
//     (review 261001 M1) — refused instead.
import type { MissionState, OperationIntentRecord } from "./mission-state-types.js";
import { deployIntentOutcomeIsUnknown } from "./release-deploy-outcome-assessment.js";
import {
  type ManualDeployAnswer,
  type ResolveUnknownDeployResult,
  refuse,
} from "./release-deploy-outcome-manual-resolution-types.js";

export function candidateOf(intent: OperationIntentRecord): string | undefined {
  const value = intent.params.candidate_sha;
  return typeof value === "string" ? value.toLowerCase() : undefined;
}

function deployHashOf(intent: OperationIntentRecord): string | undefined {
  const value = intent.params.release_deploy_hash;
  return typeof value === "string" ? value : undefined;
}

export type ResolutionTargets =
  | { ok: true; matching: OperationIntentRecord[]; retractable: OperationIntentRecord[] }
  | { ok: false; refusal: ResolveUnknownDeployResult };

export function selectResolutionTargets(input: {
  state: Pick<MissionState, "intents" | "results">;
  missionId: string;
  candidate: string;
  outcome: ManualDeployAnswer;
  currentDeployHash: string | undefined;
}): ResolutionTargets {
  const { state, candidate, currentDeployHash } = input;
  const deploys = Object.values(state.intents)
    .filter((intent) => intent.operation_kind === "deploy")
    .sort((a, b) => a.seq - b.seq);
  const unknown = deploys.filter((intent) =>
    deployIntentOutcomeIsUnknown(intent, state.results[intent.operation_id]?.outcome),
  );
  const matching = unknown.filter((intent) => candidateOf(intent) === candidate);
  const answeredDeployed =
    input.outcome === "not-deployed"
      ? deploys.filter(
          (intent) =>
            candidateOf(intent) === candidate &&
            state.results[intent.operation_id]?.manual_resolution?.answer === "deployed",
        )
      : [];
  const retractable = answeredDeployed.filter((intent) => {
    const hash = deployHashOf(intent);
    return hash !== undefined && hash === currentDeployHash;
  });

  if (matching.length === 0 && retractable.length === 0) {
    if (answeredDeployed.length > 0) {
      return {
        ok: false,
        refusal: refuse(
          "manual_answer_not_in_force",
          `Câu trả lời \`deployed\` đã ghi cho candidate ${candidate} không còn hiệu lực: config.release ` +
            "hiện tại khác lúc deploy đó chạy (deploy hash lệch" +
            `${currentDeployHash === undefined ? ", hoặc không đọc được config.release" : ""}), nên \`run\` ` +
            "không dùng nó nữa — rút lại không đổi được gì. Khôi phục config.release như lúc trả lời rồi chạy " +
            "lại lệnh này, hoặc kiểm tay môi trường đích; bỏ mission ⇒ `avatar autopilot archive`.",
        ),
      };
    }
    if (unknown.length === 0) {
      return {
        ok: false,
        refusal: refuse(
          "nothing_unknown",
          `Mission ${input.missionId} không có deploy nào ở trạng thái không xác định.`,
        ),
      };
    }
    const named = [...new Set(unknown.map((i) => candidateOf(i) ?? "(không gắn candidate)"))];
    return {
      ok: false,
      refusal: refuse(
        "candidate_mismatch",
        `Không có deploy không xác định nào cho candidate ${candidate}. Deploy đang treo thuộc: ` +
          `${named.join(", ")}. Kiểm tay đúng bản đó rồi truyền đúng sha.`,
      ),
    };
  }

  if (input.outcome === "deployed" && matching.some((i) => deployHashOf(i) !== currentDeployHash)) {
    return {
      ok: false,
      refusal: refuse(
        "deploy_hash_mismatch",
        "config.release hiện tại khác lúc deploy đang treo chạy (deploy hash lệch" +
          `${currentDeployHash === undefined ? ", hoặc không đọc được config.release" : ""}) — ` +
          "trả lời deployed sẽ không được dùng và `run` sẽ deploy lại. Khôi phục config.release như lúc " +
          "deploy rồi trả lời lại, hoặc trả lời not-deployed.",
      ),
    };
  }
  return { ok: true, matching, retractable };
}
